import React, { RefObject, useEffect, useRef, useState } from 'react';
import Table from '../../../components/Table';
import Loader from '../../../components/Loader';
import { calculateTableCharLimit } from 'src/pages/sharedPageUtils';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import useWindowSize from 'src/hooks/useWindowSize';
import ServiceRequestStatusDropdown from 'src/pages/Suite/KeyPickupRequest/KeyPickupServiceRequestStatusDropdown';
import { toast } from 'react-toastify';
import { fetchkeypickupServiceRequests, saveStatus } from 'src/services/client';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import AttachmentViewer from 'src/components/AttachmentViewer/AttachmentViewer';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';

interface KeyPickupServiceRequestListProps {
  suiteId: string;
}

const KeyPickupServiceRequestList = ({
  suiteId,
}: KeyPickupServiceRequestListProps) => {
  const userId = localStorage.getItem('userId');

  const tableRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [characterLimit, setCharacterLimit] = useState<number>(80);
  const [serviceRequests, setServiceRequests] = useState<IServiceRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
  const [updatingRequestId, setUpdatingRequestId] = useState<string | null>(
    null
  );
  const [showImages, setShowImages] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  // Function to show images in lightbox
  const handleShowImages = (data: IServiceRequest) => {
    if (data && data.attachment) {
      try {
        const parsedAttachment = JSON.parse(data.attachment);
        setSelectedImages(parsedAttachment);
        setShowImages(true);
        setSelectedIndex(0);
      } catch (e) {
        console.error('Error parsing attachment data:', e);
        toast.error('Error displaying images');
      }
    }
  };

  const loadKeyPickupServiceRequests = async () => {
    try {
      setIsLoading(true);
      const response = await fetchkeypickupServiceRequests(suiteId);

      // Check if response exists and has status property.
      if (!response || !('status' in response) || response.status !== 200) {
        console.error('API returned an unsuccessful status:', response);
        toast.error('Failed to fetch service requests');
        setServiceRequests([]);
        return;
      }

      const data: IServiceRequest[] = await response.json();
      setServiceRequests(data);
    } catch (error) {
      console.error('Error in loadKeyPickupServiceRequests:', error);
      toast.error('Failed to fetch service requests');
      setServiceRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle status change with API call.
  const handleStatusChange = async (status: { name: string }, id: string) => {
    try {
      setIsUpdatingStatus(true);
      setUpdatingRequestId(id);

      const updateResponse = await saveStatus(id, {
        name: status.name,
        LoginUserId: userId,
      });

      if (
        !updateResponse ||
        !('status' in updateResponse) ||
        updateResponse.status !== 200
      ) {
        console.error(
          'Status update API returned an unsuccessful status:',
          updateResponse
        );
        toast.error('Failed to update status');
        return;
      }

      toast.success(`Status updated to ${status.name}`);
      await loadKeyPickupServiceRequests();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
      setUpdatingRequestId(null);
    }
  };

  const { windowSize } = useWindowSize();

  useEffect(() => {
    const newLimit: number = calculateTableCharLimit(tableRef);

    if (newLimit !== characterLimit) {
      setCharacterLimit(newLimit);
    }
  }, [windowSize]);

  useEffect(() => {
    loadKeyPickupServiceRequests();
  }, [suiteId]);

  if (isLoading) {
    return (
      <div ref={tableRef}>
        <h3 style={{ marginTop: '2%' }}>Service Requests</h3>
        <Loader center={true} />
      </div>
    );
  }

  return (
    <div ref={tableRef} style={{ marginBottom: '2rem' }}>
      <h3 style={{ marginTop: '2%' }}>Inspection Items</h3>
      <Table
        defaultSort={{
          accessor: 'createdDate',
          direction: 'ascending',
        }}
        columns={[
          {
            label: 'Room/Area',
            accessor: 'area.name',
            sortable: true,
            style: { width: '12rem' },
            minWidth: '12rem',
          },
          {
            label: 'Asset',
            accessor: 'itemName',
            sortable: true,
            style: { width: '10rem' },
            minWidth: '10rem',
          },
          {
            label: 'Description',
            accessor: 'description',
            textOverflow: true, // Enable text overflow for collapsed state.
            sortable: true,
            minWidth: '22rem', // Reduced from 30rem.
            style: { width: '25%' }, // Set a percentage width.
            cell: (data: IServiceRequest) => {
              const description: string = data.description?.trim() ?? '';
              return (
                <ExpandableText
                  text={description}
                  characterLimit={characterLimit}
                />
              );
            },
          },
          {
            label: 'Status',
            showOverflow: true,
            accessor: 'status',
            style: { width: '15rem' },
            minWidth: '15rem',
            xsHide: true,
            sortable: true,
            cell: (data: IServiceRequest) => (
              <ServiceRequestStatusDropdown
                serviceRequest={data}
                handleStatusChange={handleStatusChange}
                isDisabled={isUpdatingStatus && updatingRequestId === data.id}
              />
            ),
          },
          {
            label: 'Photos',
            accessor: 'attachment',
            style: { width: '7rem' },
            minWidth: '7rem',
            sortable: false,
            cell: (data: IServiceRequest) => (
              <AttachmentViewer data={data} onShowImages={handleShowImages} />
            ),
          },
        ]}
        data={serviceRequests}
        headerTop={undefined}
      />
      {showImages && (
        <ReusableLightbox
          isOpen={showImages}
          images={selectedImages}
          initialIndex={selectedIndex || 0}
          onClose={() => setShowImages(false)}
          onIndexChange={(index) => setSelectedIndex(index)}
        />
      )}
    </div>
  );
};

export default KeyPickupServiceRequestList;
